import { FC, Fragment, ReactNode, useMemo } from 'react';
import { EMBEDDED_STYLE_RULES } from './style.rules';
import { EmbeddedStyleRule, EmbeddedStyleProps, EmbeddedStyleStringTag } from './types';
import { closeTag, openTag } from './utils';

/**
 * Recursively converts a string containing placeholder tags into a list of
 * `ReactNode`s, wrapping the text between each matching open/close tag with
 * the corresponding component from `EMBEDDED_STYLE_RULES`.
 *
 * On each call it picks the left-most matching tag across all rules, splits
 * the input in `before` / `inner` / `after` and recurses on each part: this
 * allows nested placeholders and multiple occurrences of different rules to be
 * resolved in a single pass.
 *
 * @param {string} input - Source string that may contain placeholder tags.
 * @returns {ReactNode[]} Ordered list of text and wrapped React nodes.
 */
const parseEmbeddedStyle = (input: string): ReactNode[] => {
  let matchedRule:
    | (EmbeddedStyleRule & { tagStart: EmbeddedStyleStringTag; tagStop: EmbeddedStyleStringTag })
    | undefined;
  let startIndex = -1;
  let stopIndex = -1;

  EMBEDDED_STYLE_RULES.forEach(rule => {
    const openTagString = openTag(rule.tagName);
    const closeTagString = closeTag(rule.tagName);

    const ruleStart = input.indexOf(openTagString);
    if (ruleStart === -1) {
      return;
    }
    const ruleStop = input.indexOf(closeTagString, ruleStart + openTagString.length);
    if (ruleStop === -1) {
      return;
    }
    if (startIndex === -1 || ruleStart < startIndex) {
      matchedRule = { ...rule, tagStart: openTagString, tagStop: closeTagString };
      startIndex = ruleStart;
      stopIndex = ruleStop;
    }
  });

  if (!matchedRule) {
    return input ? [input] : [];
  }

  const before = input.slice(0, startIndex);
  const inner = input.slice(startIndex + matchedRule.tagStart.length, stopIndex);
  const after = input.slice(stopIndex + matchedRule.tagStop.length);
  const { Component } = matchedRule;

  return [
    ...parseEmbeddedStyle(before),
    <Component key={`${startIndex}-${stopIndex}`}>{parseEmbeddedStyle(inner)}</Component>,
    ...parseEmbeddedStyle(after),
  ];
};

/**
 * EmbeddedStyle parses a string containing placeholder tags
 * (e.g. `<i>...</i>`, `<b>...</b>`, `<strong>...</strong>`) and wraps the text
 * between matching open/close tags with the corresponding inline component
 * declared in `EMBEDDED_STYLE_RULES`.
 *
 * Supported tags are listed in `EmbeddedStylePlaceholderName`. To add a new
 * style add a new entry to that enum and a new rule in `EMBEDDED_STYLE_RULES`
 * with the wrapper component.
 *
 * Non-string children are returned as-is.
 *
 * @param {ReactNode} [children] - Text containing the embedded style tags, or any other ReactNode (passed through).
 *
 * @returns {JSX.Element} The rendered string with the placeholder pairs replaced by their wrapper components.
 *
 * @example
 * // Renders: "Click <i>here</i> to <b>continue</b>"
 * // as:     "Click *here* to **continue**"
 * <EmbeddedStyle>
 *   {t('myKey')}
 * </EmbeddedStyle>
 */
export const EmbeddedStyle: FC<EmbeddedStyleProps> = ({ children }) => {
  const parsedChildren = useMemo(() => {
    if (typeof children !== 'string') {
      return children;
    }
    return parseEmbeddedStyle(children).map((node, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Fragment key={index}>{node}</Fragment>
    ));
  }, [children]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{parsedChildren}</>;
};
